import crews from "csi/access/crews";
import reflection from "csi/reflection";
import reflectionHints from "csi/reflectionHints";
import Cache, { rootCache } from "csi/state/Cache";
import { RootStore } from "csi/state/rootStore";
import { C8Node, C8NodeType, cnl } from "csi/typings";
import xml from "csi/xml";
import { ModalDialogProps } from "designSystem/components/dialog/ModalDialog";
import { action, computed, makeObservable, observable } from "mobx";
import { FunctionComponent } from "react";

export type RightPaneAllowedFunction = (type: C8NodeType, subType: string | undefined, node: C8Node, rootCache: Cache) => boolean;
export type RightPaneIsRequiresOneOfRolesFunction = (type: C8NodeType) => string[];

export const RightPaneComponentAlwaysAllowed: RightPaneAllowedFunction = () => true;

interface RightPaneComponentConfig {
	/** set of security roles allowed to use component; if empty, then assume no roles are required to use component */
	requiresOneOfRoles?: string[];

	/** set of security roles allowed to use component; if empty, then assume no roles are required to use component
	 * can be used instead of requiresOneOfRoles if you need to generate a list of roles based on location type
	 */
	requiresOneOfRolesFunction?: RightPaneIsRequiresOneOfRolesFunction;

	/** indicates if pane should be closed on tab change, in addition to page navigation (default is false) */
	closeOnTabChange?: boolean;

	/** function that determines if confirmation dialog should be displayed prior to leaving selected pane */
	confirmLeaving?: () => boolean;

	/** function indicating current size of pane if resized from original viewWidth */
	getResizeWidth?: () => number;

	/** set of security roles required to use component; if empty, then assume no roles are required to use component */
	requiresAllRoles?: string[];

	/** list of node paths, originating from the current context node, to resolve when navigating to a new right pane component */
	dependencyPaths?: string[];

	/** path to component to render icon */
	icon?: string;

	/** custom logic to determine access to the component */
	isAllowed: RightPaneAllowedFunction;

	/** function called when pane is selected */
	onEnter?: VoidFunction;

	/** function called when instance is targeted in pane from outside (i.e. navigation) */
	onInstance?: (url: cnl) => Promise<void>;

	/** function called when pane is deselected */
	onLeave?: VoidFunction;

	/** indicates if component width can interactively change */
	resizable?: boolean;

	/** indicates if component should not be available on templates in data setup */
	restrictOnTemplate?: boolean;

	/** function indicating current size of pane if resized from original viewWidth */
	setResizeWidth?: (width: number) => void;

	tooltipTitle?: string;

	/** path to component to render view when icon clicked */
	view: string;

	/** width of pane to hold component; set to 0 if should be full width */
	viewWidth: number;
}

export class RightPaneComponent {
	/** configuration */
	readonly config: RightPaneComponentConfig;

	/** unique identifier and localization resource for component */
	readonly id: string;

	constructor(id: string, config: RightPaneComponentConfig) {
		this.id = id;
		this.config = config;
	}
}

type DialogComponent = FunctionComponent<Partial<ModalDialogProps>>;

export default class RightPaneStore {
	private _rootStore: RootStore;
	@observable.ref private _adHocComponent: RightPaneComponent;
	@observable private _focusRight = false;
	@observable private _selectedId: string;
	@observable private _selectedUrl: cnl;

	/** dialog to display over right pane */
	@observable.ref dialogComponent: DialogComponent;

	/** properties for dialog  */
	@observable.ref dialogProps: Record<string, any>;

	/** list of available components */
	components: RightPaneComponent[] = [];

	/** indicates if right pane width has been overridden (expanded) to full browser width */
	@observable forceFullWidth: boolean;

	/** width of selected component; if 0, then width of component defaults to that specified in the configuration */
	@observable selectedWidth = 0;

	/** component to display in right pane to override other components */
	get adHocComponent(): RightPaneComponent {
		return this._adHocComponent;
	}

	/** indicates right pane is in focus so rest of page should be restricted */
	get focusRight(): boolean {
		return this._focusRight;
	}

	/** indicates if tooltip is to appear */
	get showTooltip(): boolean {
		if (this.selected && !this.selected?.config.icon) {
			return false;
		}
		return true;
	}

	/** indicates if right pane should occupy full browser width */
	@computed get isFullWidth(): boolean {
		const { forceFullWidth, selected } = this;
		return !!selected && (forceFullWidth || !selected.config.viewWidth);
	}

	/** selected component */
	@computed get selected(): RightPaneComponent {
		const { _adHocComponent, components, _selectedId } = this;
		return _adHocComponent || components.find(({ id }) => id === _selectedId);
	}

	/** selected id */
	get selectedId(): string {
		return this._selectedId;
	}

	/** initially selected url in selected right pane component */
	get selectedUrl(): string {
		return this._selectedUrl;
	}

	/** expand selected component pane */
	@action expandSelected = (): void => {
		this.forceFullWidth = true;
	};

	/** collapse selected component pane */
	@action collapseSelected = (): void => {
		this.forceFullWidth = false;
	};

	/** close dialog over right pane */
	@action closeDialog = (): void => {
		this.dialogComponent = null;
		this.dialogProps = null;
	};

	/** close selected component pane */
	@action closeSelected = (): void => {
		this.selected?.config.onLeave?.();
		this._adHocComponent = null;
		this._selectedId = null;
		this._selectedUrl = null;
		this.forceFullWidth = false;
	};

	/** open dialog over right pane */
	@action openDialog<T extends ModalDialogProps, U extends Partial<T>>(component: FunctionComponent<T>, properties: U): void {
		const { onClose } = properties;
		this.dialogComponent = component;
		this.dialogProps = {
			...properties,
			open: true,
			onClose: (): void => {
				onClose?.();
				this.closeDialog();
			},
		};
	}

	/** set ad-hoc component to take over right pane */
	@action setAdHocComponent = (component: RightPaneComponent): void => {
		this.closeSelected();
		this._adHocComponent = component;
		this._postSetSelected();
	};

	/** indicate if (user's) focus should be on right pane */
	@action setFocusRight = (value: boolean): void => {
		this._focusRight = value;
	};

	/** select given component */
	@action setSelected = (id: string): void => {
		this.closeSelected();
		this._selectedId = id;
		this._postSetSelected();
	};

	/** select instance in selected component */
	@action setSelectedInstance = async (url: cnl): Promise<void> => {
		await this.selected?.config.onInstance?.(url);

		// FIX: also store url as separate property to support Dojo views
		// as it needs to be available when view is created
		this._selectedUrl = url;
	};

	/** add component to right pane */
	add = (component: RightPaneComponent): void => {
		this.components.push(component);
	};

	/** determine components that are available for the given node */
	getAvailableComponents = (node: C8Node): RightPaneComponent[] => {
		if (!node || xml.nodeIsArchived(node)) {
			return [];
		}

		const type = node.$Type;
		const subtype = reflection.getSubtypeName(node.Subtype);
		const isTemplate = reflectionHints.isTemplateNode(node);

		return this.components.filter((component) => {
			const { requiresOneOfRoles, requiresOneOfRolesFunction, requiresAllRoles, isAllowed, restrictOnTemplate } = component.config;
			const isInRoles = requiresOneOfRoles ?? requiresOneOfRolesFunction?.(type);
			const validType = isAllowed(type, subtype, node, rootCache);
			const validTemplate = !restrictOnTemplate || !isTemplate;
			return validType && validTemplate && (!isInRoles || crews.isInRoles(isInRoles, node)) && (!requiresAllRoles || crews.isInAllRoles(requiresAllRoles, node));
		});
	};

	/** processing to do after pane is selected */
	private _postSetSelected = (): void => {
		const { selected } = this;
		selected?.config.onEnter?.();
		this.selectedWidth = selected?.config.getResizeWidth?.() || 0;
	};

	constructor(rootStore: RootStore) {
		makeObservable(this);
		this._rootStore = rootStore;
	}
}
